/**
 * Hummingbird Plan & Bucket Definitions
 * 
 * Tiered usage billing model with plan tiers and usage buckets.
 * Inspired by email marketing pricing (Mailchimp, ConvertKit, etc.)
 * 
 * @module lib/plans
 */

// =============================================================================
// Types
// =============================================================================

/** Plan tier identifiers */
export type PlanId = 'free' | 'starter' | 'pro' | 'elite' | 'enterprise';

/** Billing interval */
export type BillingInterval = 'monthly' | 'annual';

/**
 * Usage bucket within a plan tier
 * Each bucket has a request range and corresponding price
 */
export interface UsageBucket {
  /** Plan tier this bucket belongs to */
  plan: PlanId;
  /** Minimum requests for this bucket (inclusive) */
  minRequests: number;
  /** Maximum requests for this bucket (inclusive) */
  maxRequests: number;
  /** Monthly price in cents */
  monthlyPriceCents: number;
  /** Intro price in cents (first period ~20% off) */
  introPriceCents: number;
  /** Annual price in cents (17% off = 10 months) */
  annualPriceCents: number;
  /** Stripe lookup key for this price */
  stripeLookupKey: string;
  /** Human-readable bucket label */
  label: string;
}

/**
 * Plan tier definition
 */
export interface PlanTier {
  /** Unique plan identifier */
  id: PlanId;
  /** Display name */
  name: string;
  /** Short description */
  description: string;
  /** Minimum requests in this tier */
  minRequests: number;
  /** Maximum requests in this tier (ceiling) */
  maxRequests: number;
  /** Bucket increment size */
  bucketIncrement: number;
  /** Features included in this tier */
  features: PlanFeatures;
  /** Plan order for comparison (higher = more features) */
  order: number;
}

/**
 * Features available at a plan tier
 */
export interface PlanFeatures {
  /** Maximum API keys */
  maxApiKeys: number;
  /** Maximum team members */
  maxTeamMembers: number;
  /** Rate limit (requests per second) */
  rateLimit: number;
  /** Has access to premium APIs */
  premiumApis: boolean;
  /** Has access to bulk endpoints */
  bulkEndpoints: boolean;
  /** Support level */
  supportLevel: 'community' | 'email_48h' | 'email_24h' | 'priority_4h' | 'dedicated';
  /** Has phone support */
  phoneSupport: boolean;
  /** Has SSO/SAML */
  sso: boolean;
  /** SLA percentage (null = no SLA) */
  sla: number | null;
}

/**
 * Bonus action for free tier gamification
 */
export interface BonusAction {
  /** Unique action identifier */
  id: string;
  /** Display name */
  name: string;
  /** Description of how to complete */
  description: string;
  /** Bonus requests earned */
  bonusRequests: number;
  /** Icon name (Lucide) */
  icon: string;
}

// =============================================================================
// Plan Tier Definitions
// =============================================================================

export const PLAN_TIERS: Record<PlanId, PlanTier> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'For trying out the platform',
    minRequests: 0,
    maxRequests: 2000, // 1K base + 1K bonus (LIFETIME, not monthly)
    bucketIncrement: 0,
    order: 0,
    features: {
      maxApiKeys: 1,
      maxTeamMembers: 1,
      rateLimit: 5, // 50 req/10s burst limit
      premiumApis: false,
      bulkEndpoints: false,
      supportLevel: 'community',
      phoneSupport: false,
      sso: false,
      sla: null,
    },
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'For individual developers & small projects',
    minRequests: 10001,
    maxRequests: 50000,
    bucketIncrement: 5000,
    order: 1,
    features: {
      maxApiKeys: 5,
      maxTeamMembers: 3,
      rateLimit: 25, // 250 req/10s burst limit
      premiumApis: false,
      bulkEndpoints: false,
      supportLevel: 'email_48h',
      phoneSupport: false,
      sso: false,
      sla: null,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'For growing applications & startups',
    minRequests: 50001,
    maxRequests: 250000,
    bucketIncrement: 10000,
    order: 2,
    features: {
      maxApiKeys: 20,
      maxTeamMembers: 10,
      rateLimit: 50, // 500 req/10s burst limit
      premiumApis: true,
      bulkEndpoints: true,
      supportLevel: 'email_24h',
      phoneSupport: false,
      sso: false,
      sla: null,
    },
  },
  elite: {
    id: 'elite',
    name: 'Elite',
    description: 'For high-volume production workloads',
    minRequests: 250001,
    maxRequests: 2000000,
    bucketIncrement: 25000,
    order: 3,
    features: {
      maxApiKeys: 50,
      maxTeamMembers: 25,
      rateLimit: 250, // 2500 req/10s burst limit
      premiumApis: true,
      bulkEndpoints: true,
      supportLevel: 'priority_4h',
      phoneSupport: true,
      sso: false,
      sla: 99.9,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations with custom needs',
    minRequests: 2000001,
    maxRequests: Infinity,
    bucketIncrement: 100000,
    order: 4,
    features: {
      maxApiKeys: -1, // Unlimited
      maxTeamMembers: -1, // Unlimited
      rateLimit: -1, // Custom
      premiumApis: true,
      bulkEndpoints: true,
      supportLevel: 'dedicated',
      phoneSupport: true,
      sso: true,
      sla: 99.99,
    },
  },
};

// =============================================================================
// Free Tier Bonus Actions
// =============================================================================

export const FREE_TIER_BASE_REQUESTS = 1000;
export const FREE_TIER_MAX_BONUS = 1000;

export const BONUS_ACTIONS: BonusAction[] = [
  {
    id: 'verify_phone',
    name: 'Verify Phone',
    description: 'Add and verify your phone number',
    bonusRequests: 400,
    icon: 'Phone',
  },
  {
    id: 'add_payment_method',
    name: 'Add Payment Method',
    description: 'Add a credit card for future upgrades',
    bonusRequests: 300,
    icon: 'CreditCard',
  },
  {
    id: 'complete_profile',
    name: 'Complete Profile',
    description: 'Fill out your company and use case',
    bonusRequests: 100,
    icon: 'User',
  },
  {
    id: 'create_api_key',
    name: 'Create API Key',
    description: 'Generate your first API key',
    bonusRequests: 100,
    icon: 'Key',
  },
  {
    id: 'first_api_call',
    name: 'Make First Call',
    description: 'Successfully call an API endpoint',
    bonusRequests: 100,
    icon: 'Zap',
  },
];

// =============================================================================
// Usage Bucket Generation
// =============================================================================

/**
 * Generate all usage buckets for a plan tier
 */
function generateBucketsForPlan(plan: PlanId): UsageBucket[] {
  const tier = PLAN_TIERS[plan];
  if (plan === 'free') return [];

  const buckets: UsageBucket[] = [];
  
  // Base prices in cents for each tier
  const basePrices: Record<PlanId, number> = {
    free: 0,
    starter: 599,     // $5.99
    pro: 1299,        // $12.99
    elite: 3199,      // $31.99 (matches Pro ceiling)
    enterprise: 9999, // $99.99 (buyer psychology)
  };

  // Price increment per bucket in cents
  const priceIncrements: Record<PlanId, number> = {
    free: 0,
    starter: 100,    // $1.00 per 5K
    pro: 100,        // $1.00 per 10K
    elite: 100,      // $1.00 per 25K
    enterprise: 400, // $4.00 per 100K
  };

  let currentMin = tier.minRequests;
  let currentMax = currentMin + tier.bucketIncrement - 1;
  let bucketIndex = 0;

  while (currentMin <= tier.maxRequests) {
    // Cap the max at tier ceiling
    if (currentMax > tier.maxRequests) {
      currentMax = tier.maxRequests;
    }

    const monthlyPrice = basePrices[plan] + (bucketIndex * priceIncrements[plan]);
    const introPrice = Math.round(monthlyPrice * 0.8); // 20% off
    const annualPrice = Math.round(monthlyPrice * 10); // 17% off (10 months)

    // Format the lookup key
    const maxK = Math.round(currentMax / 1000);
    const lookupKey = `${plan}_${maxK}k_monthly`;

    buckets.push({
      plan,
      minRequests: bucketIndex === 0 ? 0 : currentMin, // First bucket starts at 0 for easier matching
      maxRequests: currentMax,
      monthlyPriceCents: monthlyPrice,
      introPriceCents: introPrice,
      annualPriceCents: annualPrice,
      stripeLookupKey: lookupKey,
      label: `${formatNumber(currentMax)} requests`,
    });

    // Move to next bucket
    currentMin = currentMax + 1;
    currentMax = currentMin + tier.bucketIncrement - 1;
    bucketIndex++;

    // Safety limit for Enterprise
    if (plan === 'enterprise' && bucketIndex >= 6) break;
  }

  return buckets;
}

/**
 * Format number with commas
 */
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`.replace('.0M', 'M');
  }
  if (num >= 1000) {
    return `${Math.round(num / 1000)}K`;
  }
  return num.toString();
}

// =============================================================================
// All Usage Buckets (Generated)
// =============================================================================

export const USAGE_BUCKETS: UsageBucket[] = [
  ...generateBucketsForPlan('starter'),
  ...generateBucketsForPlan('pro'),
  ...generateBucketsForPlan('elite'),
  ...generateBucketsForPlan('enterprise'),
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the plan order for comparison
 */
export function getPlanOrder(planId: PlanId): number {
  return PLAN_TIERS[planId]?.order ?? 0;
}

/**
 * Check if one plan is higher tier than another
 */
export function isHigherTier(planA: PlanId, planB: PlanId): boolean {
  return getPlanOrder(planA) > getPlanOrder(planB);
}

/**
 * Get buckets for a specific plan
 */
export function getBucketsForPlan(planId: PlanId): UsageBucket[] {
  return USAGE_BUCKETS.filter(b => b.plan === planId);
}

/**
 * Find the bucket that matches a request count
 * Returns the smallest bucket that can accommodate the usage
 */
export function getBucketForUsage(requests: number): UsageBucket | null {
  return USAGE_BUCKETS.find(b => requests <= b.maxRequests) || null;
}

/**
 * Find the appropriate bucket considering current plan and bucket (sticky)
 * 
 * Rules:
 * 1. Cannot downgrade to lower tier (plan order)
 * 2. Cannot downgrade to smaller bucket (sticky buckets)
 * 3. Must accommodate current usage
 */
export function getTargetBucket(
  currentUsage: number,
  currentPlan: PlanId,
  currentBucketMax: number
): UsageBucket | null {
  const currentPlanOrder = getPlanOrder(currentPlan);

  // Find buckets that:
  // 1. Can accommodate usage
  // 2. Are in same or higher tier
  // 3. Are same or larger bucket
  const eligibleBuckets = USAGE_BUCKETS.filter(b =>
    currentUsage <= b.maxRequests &&
    getPlanOrder(b.plan) >= currentPlanOrder &&
    b.maxRequests >= currentBucketMax
  );

  // Return the smallest eligible bucket
  return eligibleBuckets[0] || null;
}

/**
 * Check if usage exceeds current bucket, requiring an upgrade
 */
export function needsBucketUpgrade(
  currentUsage: number,
  currentBucketMax: number
): boolean {
  return currentUsage > currentBucketMax;
}

/**
 * Check if usage exceeds tier ceiling, requiring tier upgrade
 */
export function needsTierUpgrade(
  currentUsage: number,
  currentPlan: PlanId
): boolean {
  const tier = PLAN_TIERS[currentPlan];
  return currentUsage > tier.maxRequests;
}

/**
 * Get the next tier up from current plan
 */
export function getNextTier(currentPlan: PlanId): PlanId | null {
  const order = getPlanOrder(currentPlan);
  const nextTier = Object.values(PLAN_TIERS).find(t => t.order === order + 1);
  return nextTier?.id || null;
}

/**
 * Calculate total free tier requests including bonuses
 */
export function calculateFreeTierTotal(completedBonuses: string[]): number {
  let total = FREE_TIER_BASE_REQUESTS;
  
  for (const action of BONUS_ACTIONS) {
    if (completedBonuses.includes(action.id)) {
      total += action.bonusRequests;
    }
  }
  
  return Math.min(total, FREE_TIER_BASE_REQUESTS + FREE_TIER_MAX_BONUS);
}

/**
 * Format price from cents to display string
 */
export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Get display price for a bucket (considers intro pricing)
 */
export function getBucketDisplayPrice(
  bucket: UsageBucket,
  isIntro: boolean,
  interval: BillingInterval
): { price: string; originalPrice?: string } {
  if (interval === 'annual') {
    const price = formatPrice(bucket.annualPriceCents);
    return { price: `${price}/yr` };
  }

  const monthlyPrice = formatPrice(bucket.monthlyPriceCents);
  
  if (isIntro) {
    const introPrice = formatPrice(bucket.introPriceCents);
    return {
      price: `${introPrice}/mo`,
      originalPrice: `${monthlyPrice}/mo`,
    };
  }

  return { price: `${monthlyPrice}/mo` };
}

// =============================================================================
// Stripe Price ID Mapping
// =============================================================================

/**
 * Get Stripe price ID from bucket lookup key
 * In production, these would come from environment variables
 */
export function getStripePriceId(
  lookupKey: string,
  interval: BillingInterval = 'monthly'
): string {
  // Replace _monthly with _annual if needed
  const key = interval === 'annual'
    ? lookupKey.replace('_monthly', '_annual')
    : lookupKey;
  
  // In production, this would be a lookup table from env vars
  // For now, return the lookup key (Stripe can resolve by lookup_key)
  return key;
}

/**
 * Get all Stripe price lookup keys for creating prices
 */
export function getAllStripeLookupKeys(): string[] {
  const keys: string[] = [];

  for (const bucket of USAGE_BUCKETS) {
    keys.push(bucket.stripeLookupKey);
    keys.push(bucket.stripeLookupKey.replace('_monthly', '_annual'));
  }

  return keys;
}

// =============================================================================
// Compatibility Helpers (for existing pages)
// =============================================================================

/**
 * Plan rate limits mapped from PLAN_TIERS for backward compatibility
 * Used by DashboardPage and UsagePage
 */
export const PLAN_RATE_LIMITS: Record<PlanId, { requestsPerMonth: number; requestsPerSecond: number }> = {
  free: {
    requestsPerMonth: FREE_TIER_BASE_REQUESTS + FREE_TIER_MAX_BONUS,
    requestsPerSecond: PLAN_TIERS.free.features.rateLimit,
  },
  starter: {
    requestsPerMonth: PLAN_TIERS.starter.maxRequests,
    requestsPerSecond: PLAN_TIERS.starter.features.rateLimit,
  },
  pro: {
    requestsPerMonth: PLAN_TIERS.pro.maxRequests,
    requestsPerSecond: PLAN_TIERS.pro.features.rateLimit,
  },
  elite: {
    requestsPerMonth: PLAN_TIERS.elite.maxRequests,
    requestsPerSecond: PLAN_TIERS.elite.features.rateLimit,
  },
  enterprise: {
    requestsPerMonth: -1, // Unlimited
    requestsPerSecond: -1, // Custom
  },
};

/**
 * Check if a plan is a paid plan
 */
export function isPaidPlan(planId: PlanId): boolean {
  return planId !== 'free';
}

/**
 * Check if a plan allows inviting team members
 * Only Pro+ plans (pro, elite, enterprise) can invite team members
 */
export function canInviteTeamMembers(planId: PlanId): boolean {
  return getPlanOrder(planId) >= getPlanOrder('pro');
}